import React from "react";
import { ActivityData } from "../../types";
import star from "../../assets/icons/star.svg";
import { Clock } from "lucide-react";
import { useAppState } from "../../hooks/useAppState";

interface ActivityItemProps {
  data: ActivityData;
  handleView: any;
  handleReportIssue?: (data: ActivityData) => void;
  isSelected?: boolean; // Add this prop
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  data,
  handleView,
  handleReportIssue,
  isSelected = false, // Use prop, default false
}) => {
  const { user } = useAppState();

  // Display the right user data based on current user's role
  const isExpert = user?.role === "Expert";
  // If current user is expert, display customer data. If customer, display expert data
  const displayData = isExpert
    ? data.customerData || data.userData
    : data.expertData || data.userData;
  // Get display properties with proper fallbacks
  const displayName =
    displayData?.name ||
    displayData?.displayName ||
    data.name ||
    "Unknown User";
  const displayEmail = data?.email || data.email || "";
  const displayId = data.activityId || data.id || "N/A";
  const displayImage =
    displayData?.profileImageUrl || displayData?.photoURL || data.profileImage;
  const displayRating = isExpert
    ? data?.customerRating || "0"
    : data?.customerRating || "0";
  const displayActivityName = isExpert
    ? data?.expertActivityName || "0"
    : data?.customerActivityName || "0";
  const displayStatus = data.status || "completed";
  console.log(data, "data status");
  // Format cost/earnings based on role
  const costLabel = isExpert ? "Earnings" : "Amount";
  const displayAmount =
    data.earnings ||
    (data.cost ? `$${parseFloat(String(data.cost)).toFixed(2)}` : "$0.00");

  const getInitialsImage = (name: string) => {
    if (!name) return null;
    const initials = name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase());
    return (
      <div className="w-[98px] h-[98px] bg-[#F15D18] rounded-lg overflow-hidden">
        <div className="flex items-center justify-center h-full text-white font-bold text-xl">
          {initials.join("")}
        </div>
      </div>
    );
  };
  const getStatusColor = () => {
    const statusMap: Record<string, string> = {
      "Issue reported": "red",
      "In Progress": "yellow",
      Completed: "green",
      completed: "green",
      Cancelled: "gray",
      active: "green",
      Pending: "amber",
    };
    const defaultColor = "gray";
    return statusMap[displayStatus] || defaultColor;
  };

  const getDisplayStatus = (status: string) => {
    if (status === "Issue reported") return "Issue Raised";
    return status;
  };

  const statusColor = getStatusColor();

  const handleViewClick = (e: React.MouseEvent, data: ActivityData) => {
    e.stopPropagation();
    // No setIsSelected here
    const wrappedData = {
      ...data,
      name: displayName,
      email: displayEmail,
      activityId: displayId,
      profileImage: displayImage,
      ratings: displayRating,
      // Remove onModalClose
    };
    handleView(wrappedData);
  };
  return (
    <div
      className={`flex flex-col w-full ${
        isSelected ? "bg-gray-100" : "bg-white"
      } rounded-[16px] border border-gray-200 p-3 shadow-sm mb-3 hover:shadow-md transition-shadow relative group`}
    >
      <div className="flex justify-between w-full">
        {/* Left section - User info */}
        <div className="flex space-x-4">
          <div className="w-[98px] h-[98px] rounded-lg overflow-hidden">
            {displayImage ? (
              <img
                src={displayImage}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              getInitialsImage(displayName)
            )}
          </div>
          <div className="flex flex-col mt-2">
            <h3 className="nunito-semibold text-[24px] text-[#0A0A0A]">
              {displayName}
            </h3>
            <p className="text-[#919191] text-[14px] nunito-regular">
              {displayEmail}
            </p>
            <div className="flex mt-4 items-center gap-1 text-[#696969] text-[12px] nunito-regular">
              <Clock className="w-[16px] h-[16px]" />
              <span>{data.startTime}</span>
            </div>
          </div>
        </div>

        {/* Right section - Activity details */}
        <div className="flex flex-col justify-between h-[98px] mr-5">
          <div className="flex space-x-10 mt-2 ">
            {/* Activity Name */}
            <div className="flex flex-col items-start">
              <p className="text-[#696969] text-[16px] nunito-medium">
                Activity Name
              </p>{" "}
              <div className="flex items-start">
                <span className="nunito-regular text-[#696969] text-[14px] ">
                  {displayActivityName}
                </span>
              </div>
            </div>
            {/* Rating */}
            <div className="flex flex-col items-start">
              <p className="text-[#696969] text-[16px] nunito-medium">Rating</p>{" "}
              <div className="flex items-start">
                <span className="nunito-regular text-[#696969] text-[14px] ">
                  {displayRating}
                </span>
                <img
                  src={star}
                  className="w-[24px] h-[24px] ml-1"
                  alt="Rating star"
                />
              </div>
            </div>

            {/* Activity ID */}
            <div className="flex flex-col items-start">
              <p className="text-[#696969] text-[16px] nunito-medium">
                Activity ID
              </p>
              <span className="nunito-bold text-[#F15D18] text-[16px]">
                {displayId}
              </span>
            </div>

            {/* Duration */}
            <div className="flex flex-col items-start">
              <p className="text-[#696969] text-[16px] nunito-medium">
                Duration
              </p>
              <span className="nunito-bold text-[16px] text-[#F15D18]">
                {data.duration || "N/A"}
              </span>
            </div>

            {/* Amount/Earnings */}
            <div className="flex flex-col items-start">
              <p className="text-[#696969] text-[16px] nunito-medium">
                {costLabel}
              </p>
              <span className="nunito-bold text-[16px] text-[#F15D18]">
                {displayAmount}{" "}
                {data.hasRefund === true && (
                  <span className="text-[#F15D18] ">*</span>
                )}
              </span>
            </div>

            {/* status */}
            <div className="flex flex-col items-center" style={{ minWidth: "70px", display: "inline-block" }}>
              <p className="text-[#696969] text-[16px] nunito-medium">Status</p>
              <div className="flex items-center border p-1 rounded-xl w-full justify-center">
                <span
                  className={`h-2 w-2 bg-${statusColor}-500 rounded-full mr-1`}
                ></span>{" "}
                <span
                  className={`text-${statusColor}-500 text-[10px]`}
                  
                >
                  {getDisplayStatus(displayStatus)}
                </span>
              </div>
            </div>
          </div>

          {/* Action buttons moved inside the right section */}
          <div className="flex justify-end">
            {" "}
            <div className="flex space-x-2">
              {" "}
              {user?.role === "Customer" && (
                <button
                  className={`px-4 py-1 rounded-md text-sm relative ${
                    data.hasIssue
                      ? "text-[#D9D9D9]  cursor-not-allowed group"
                      : "text-[#919191]"
                  }`}
                  onClick={() =>
                    !data.hasIssue &&
                    handleReportIssue &&
                    handleReportIssue(data)
                  }
                  disabled={data.hasIssue}
                  title={data.hasIssue ? "" : ""}
                >
                  {data.hasIssue ? "Issue Reported" : "Report Issue"}{" "}
                </button>
              )}
              <button
                className="px-4 w-[108px] py-1 bg-[#F15D18] h-[32px] text-[14px] nunito-semibold text-white rounded-[8px] hover:bg-[#D84D08] text-sm"
                onClick={(e) => handleViewClick(e, data)}
              >
                View
              </button>
            </div>{" "}
          </div>
          {data.hasRefund && (
            <div className="absolute z-10 opacity-0 group-hover:opacity-100 top-0 right-[80px] px-2 py-2 bg-[#FAFAFA] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none w-[216px]">
              <div className="text-start">
                <div className="nunito-regular text-[12px] text-[#696969] mb-1">
                  Refund is issued.
                </div>
                <div className="text-[#696969] nunito-regular text-[12px]">
                  Click on 'View' to know more details
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
